import { Link } from "react-router-dom";
import { ProgressiveImage } from "@/components/ProgressiveImage";
import { formatDistanceToNow } from "date-fns";
import { fr, enUS, nl } from "date-fns/locale";
import { MapPin, Star, Sparkles, Store } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { VerifiedProBadge } from "@/components/VerifiedProBadge";
import { Button } from "@/components/ui/button";

const DATE_LOCALES = { fr, en: enUS, nl };

/**
 * Unified supplier card for home dynamic sections.
 * @param {object} props
 * @param {boolean} [props.showNewBadge] — force or use supplier.is_new
 * @param {boolean} [props.showJoinDate]
 * @param {boolean} [props.emphasizeRating]
 */
export function SupplierHomeCard({
  supplier,
  testId,
  showNewBadge = false,
  showJoinDate = false,
  emphasizeRating = false,
}) {
  const { t, lang } = useLang();
  const name = supplier.company_name || supplier.full_name || "—";
  const initial = name.slice(0, 1).toUpperCase();
  const isPremium = !!supplier.is_premium;
  const isNew = showNewBadge || supplier.is_new;

  return (
    <article
      data-testid={testId}
      className={`matpass-card-hover group flex h-full flex-col rounded-2xl border bg-white p-5 shadow-[0_4px_20px_rgba(24,24,27,0.05)] ${
        isPremium ? "border-matpro-green/40" : "border-matpro-border hover:border-matpro-green/40"
      }`}
    >
      <div className="flex items-start gap-3">
        {supplier.logo_url ? (
          <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-matpro-border">
            <ProgressiveImage
              src={supplier.logo_url}
              alt=""
              className="h-full w-full object-cover"
              wrapperClassName="h-full w-full"
            />
          </div>
        ) : (
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-matpro-anthracite font-heading text-xl font-black text-white">
            {initial}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap gap-1">
            {isNew ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-sky-600 px-2 py-0.5 text-[10px] font-bold text-white">
                🆕 {t("card.badge_new")}
              </span>
            ) : null}
            {isPremium ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-matpro-anthracite px-2 py-0.5 text-[10px] font-bold text-matpro-green">
                <Sparkles className="h-3 w-3" />
                Premium
              </span>
            ) : null}
            {supplier.verified_pro ? <VerifiedProBadge size="xs" /> : null}
          </div>
          <h3 className="line-clamp-2 font-heading text-base font-bold text-matpro-anthracite">{name}</h3>
          {supplier.sector_label ? (
            <p className="mt-1 text-xs font-semibold text-matpro-green">{supplier.sector_label}</p>
          ) : null}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-matpro-anthracite-muted">
        {supplier.city ? (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5 text-matpro-green" />
            {supplier.city}
          </span>
        ) : null}
        {(supplier.rating?.count || 0) > 0 ? (
          <span
            className={`inline-flex items-center gap-1 ${
              emphasizeRating ? "text-base font-bold text-matpro-anthracite" : "font-semibold text-matpro-anthracite"
            }`}
          >
            <Star className="h-3.5 w-3.5 fill-matpro-green stroke-matpro-green" />
            {supplier.rating.average.toFixed(1)}
            <span className="font-normal text-matpro-anthracite-muted">({supplier.rating.count})</span>
          </span>
        ) : null}
        {showJoinDate && supplier.created_at ? (
          <span className="text-matpro-anthracite-muted">
            {formatDistanceToNow(new Date(supplier.created_at), {
              addSuffix: true,
              locale: DATE_LOCALES[lang] || fr,
            })}
          </span>
        ) : null}
      </div>

      <div className="flex-1" />

      <Button
        asChild
        className="mt-5 h-10 w-full rounded-xl bg-matpro-green font-semibold text-white hover:bg-matpro-green-dark"
      >
        <Link to={`/pro/${supplier.id}`}>
          <Store className="mr-2 h-4 w-4" />
          {t("home.featured_supplier_cta")}
        </Link>
      </Button>
    </article>
  );
}
